import React, { useState, useEffect } from "react";
import "./popupSlotClient.css";

/* ===== Config API ===== */
const RAW = (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) || process.env.REACT_APP_API_URL || "";
const API = (RAW ? RAW.replace(/\/+$/, "").replace(/\/api$/i, "") : "") || "http://localhost:5000";

/* ===== Helpers HTTP ===== */
function authHeaders() {
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function postJSON(url, body) {
  const res = await fetch(`${API}${url}`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`${url} ${res.status}: ${t}`);
  }
  return res.json().catch(() => ({}));
}
async function getJSON(url) {
  const res = await fetch(`${API}${url}`);
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`${url} ${res.status}: ${t}`);
  }
  return res.json();
}
async function getJSONAuth(url) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${API}${url}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`${url} ${res.status}: ${t}`);
  }
  return res.json();
}

/* ===== Helpers divers ===== */
function toFRDateTime(iso) {
  return new Date(iso).toLocaleString("fr-FR", { dateStyle: "full", timeStyle: "short" });
}
function toFRDate(iso) {
  return new Date(iso).toLocaleDateString("fr-FR", { dateStyle: "full" });
}
function toFRTime(iso) {
  return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}
function formatEUR(n) {
  if (n == null) return "";
  try {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(Number(n));
  } catch {
    const x = Number(n);
    return isFinite(x) ? `${x.toFixed(2)} €` : "";
  }
}
// FR -> E.164
function normalizePhone(p) {
  if (!p) return "";
  let s = String(p).trim().replace(/\s+/g, "");
  if (s.startsWith("+")) return s;
  if (s.startsWith("0")) return "+33" + s.slice(1);
  return s;
}

/* ===== Notifications immédiates (avec logs) ===== */
async function notifyNow({ client, pro, rdvISO, lieu, service, price, proSociete }) {
  const tasks = [];
  const dateTxt = toFRDate(rdvISO);
  const timeTxt = toFRTime(rdvISO);
  const prixTxt = price != null ? formatEUR(price) : "";

  // CLIENT email
  if (client.email) {
    const subject = "Votre rendez-vous est confirmé ✅";
    const lines = [
      `Bonjour ${client.prenom || ""} ${client.nom || ""}`.trim(),
      "",
      `Merci pour votre rendez-vous chez ${proSociete || pro.nom || "votre professionnel"}, le ${dateTxt} à ${timeTxt}.`,
      service ? `Prestation : ${service}${prixTxt ? ` — ${prixTxt}` : ""}` : "",
      lieu ? `Lieu : ${lieu}` : "",
      pro.phone ? `Si besoin : ${pro.phone}.` : "",
      "",
      "À très bientôt 👋",
    ].filter(Boolean);
    tasks.push(
      postJSON("/api/notifications/email", { to: client.email, subject, text: lines.join("\n") }).then(
        (r) => ({ ok: true, target: "client.email", r }),
        (e) => ({ ok: false, target: "client.email", error: String(e) })
      )
    );
  }
  // CLIENT sms
  if (client.phone) {
    const sms = [
      "RDV confirmé",
      proSociete ? `chez ${proSociete}` : "",
      `le ${dateTxt} à ${timeTxt}`,
      service ? `– ${service}` : "",
      prixTxt ? `(${prixTxt})` : "",
      pro.phone ? `Tel pro: ${pro.phone}` : "",
    ].filter(Boolean).join(" ");
    tasks.push(
      postJSON("/api/notifications/sms", { to: normalizePhone(client.phone), text: sms }).then(
        (r) => ({ ok: true, target: "client.sms", r }),
        (e) => ({ ok: false, target: "client.sms", error: String(e) })
      )
    );
  }

  // PRO email
  if (pro.email) {
    const subject = "Nouveau rendez-vous reçu 📅";
    const lines = [
      `Bonjour ${pro.nom || proSociete || "Professionnel"}`,
      "",
      `Vous avez un nouveau rendez-vous :`,
      `• Date : ${dateTxt}`,
      `• Heure : ${timeTxt}`,
      service ? `• Prestation : ${service}${prixTxt ? ` — ${prixTxt}` : ""}` : "",
      lieu ? `• Lieu : ${lieu}` : "",
      "",
      "Client :",
      `• Nom : ${client.prenom || ""} ${client.nom || ""}`.trim(),
      client.phone ? `• Téléphone : ${client.phone}` : "",
      client.email ? `• E-mail : ${client.email}` : "",
    ].filter(Boolean);
    tasks.push(
      postJSON("/api/notifications/email", { to: pro.email, subject, text: lines.join("\n") }).then(
        (r) => ({ ok: true, target: "pro.email", r }),
        (e) => ({ ok: false, target: "pro.email", error: String(e) })
      )
    );
  }

  const results = await Promise.all(tasks);
  if (localStorage.getItem("debugNotifs") === "1") {
    console.group("[notifs] résultats envois immédiats");
    results.forEach((x) => console[x.ok ? "log" : "error"](x));
    console.groupEnd();
  }
  return results;
}

/* ===== Rappels planifiés (–24h email, –1h sms) ===== */
async function scheduleReminders({ client, pro, rdvISO, lieu, service, price, proSociete }) {
  const now = Date.now();
  const when = new Date(rdvISO).getTime();
  if (Number.isNaN(when) || when <= now) return;

  const H1 = 60 * 60 * 1000;
  const H24 = 24 * H1;
  const tasks = [];

  // –24h EMAIL (client + pro)
  if (when - H24 > now) {
    const when24 = new Date(when - H24).toISOString();
    const prixTxt = price != null ? formatEUR(price) : "";
    if (client.email) {
      tasks.push({
        whenISO: when24,
        kind: "email",
        to: client.email,
        subject: "Rappel : votre rendez-vous est demain",
        text: [
          `Bonjour ${client.prenom || ""} ${client.nom || ""}`.trim(),
          "",
          `Petit rappel : rendez-vous ${proSociete ? `chez ${proSociete} ` : ""}demain à ${toFRTime(rdvISO)}.`,
          service ? `Prestation : ${service}${prixTxt ? ` — ${prixTxt}` : ""}` : "",
          lieu ? `Lieu : ${lieu}` : "",
        ].filter(Boolean).join("\n"),
      });
    }
    if (pro.email) {
      tasks.push({
        whenISO: when24,
        kind: "email",
        to: pro.email,
        subject: "Rappel : RDV prévu demain",
        text: [
          `Rappel : RDV demain ${toFRDateTime(rdvISO)}`,
          service ? `Prestation : ${service}${prixTxt ? ` — ${prixTxt}` : ""}` : "",
          lieu ? `Lieu : ${lieu}` : "",
          "",
          "Client :",
          `• ${client.prenom || ""} ${client.nom || ""}`.trim(),
          client.phone ? `• Tel : ${client.phone}` : "",
          client.email ? `• Email : ${client.email}` : "",
        ].filter(Boolean).join("\n"),
      });
    }
  }
}

const PopupSlotClient = ({
  visible,
  onClose,
  onSave,
  slotTime,
  annonceId,
  profilId,
  bookedSlots = [],
}) => {
  const [activities, setActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);

  // Infos client
  const clientNom = localStorage.getItem("nom") || "";
  const clientPrenom = localStorage.getItem("prenom") || "";
  const clientEmail = localStorage.getItem("email") || "";
  const clientPhone = localStorage.getItem("telephone") || "";
  const userId = localStorage.getItem("userId");

  const [date, setDate] = useState("");
  const [heure, setHeure] = useState("");
  const [endDate, setEndDate] = useState(null);
  const [workHours, setWorkHours] = useState([]);
  const [jourLabel, setJourLabel] = useState("");
  const [horairesDepasse, setHorairesDepasse] = useState(false);
  const [loading, setLoading] = useState(false);

  // Helpers horaires
  const safeSplit = (s, sep = ":") => (typeof s === "string" ? s.split(sep) : []);
  const parseIntSafe = (v, d = 0) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : d;
  };
  const toDateAtHHMM = (baseDate, hhmm) => {
    if (typeof hhmm !== "string" || !hhmm.includes(":")) return null;
    const [h, m] = safeSplit(hhmm).map((x) => parseIntSafe(x));
    const d = new Date(baseDate);
    d.setHours(h, m, 0, 0);
    return d;
  };
  const normalizeWorkDay = (dayObj) => {
    if (!dayObj || dayObj.isOpen === false) return { isOpen: false, slots: [] };
    if (Array.isArray(dayObj.slots) && dayObj.slots.length > 0) {
      const slots = dayObj.slots.filter((s) => s && s.start && s.end);
      return { isOpen: true, slots };
    }
    if (dayObj.start || dayObj.end) {
      return { isOpen: true, slots: [{ start: dayObj.start || "00:00", end: dayObj.end || "23:59" }] };
    }
    return { isOpen: false, slots: [] };
  };

  // Init depuis le créneau cliqué
  useEffect(() => {
    if (slotTime) {
      const d = new Date(slotTime);
      setDate(d.toISOString().slice(0, 10));
      setHeure(`${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`);
      setJourLabel(d.toLocaleDateString("fr-FR", { weekday: "long" }).toLowerCase());
    }
  }, [slotTime, visible]);

  useEffect(() => {
    if (date) {
      const d = new Date(date + "T00:00:00");
      setJourLabel(d.toLocaleDateString("fr-FR", { weekday: "long" }).toLowerCase());
    }
  }, [date]);

  // Fetch activités (avec prix si dispo) + horaires
  useEffect(() => {
    const fetchData = async () => {
      try {
        const annonce = await getJSON(`/api/annonces/${annonceId}`);
        let activitiesList = [];
        let workH = [];

        if (profilId && Array.isArray(annonce.profils)) {
          const profil = annonce.profils.find((p) => String(p._id) === String(profilId));
          if (profil && Array.isArray(profil.activities)) {
            activitiesList = profil.activities.map((act, i) => ({
              id: profil._id + "-" + i,
              name: act.name,
              price: act.price ?? act.prix ?? 0,
              duration: parseIntSafe(act.durationHours) * 60 + parseIntSafe(act.durationMinutes),
              durationHours: parseIntSafe(act.durationHours),
              durationMinutes: parseIntSafe(act.durationMinutes),
            }));
          }
          if (profil && Array.isArray(profil.workingHours)) {
            workH = profil.workingHours;
          }
        } else if (Array.isArray(annonce.professions)) {
          activitiesList = annonce.professions.flatMap((prof, pidx) =>
            (prof.activities || []).map((act, i) => ({
              id: `${pidx}-${i}`,
              name: `[${prof.profession}] ${act.name}`,
              price: act.price ?? act.prix ?? 0,
              duration: parseIntSafe(act.durationHours) * 60 + parseIntSafe(act.durationMinutes),
              durationHours: parseIntSafe(act.durationHours),
              durationMinutes: parseIntSafe(act.durationMinutes),
            }))
          );
          if (Array.isArray(annonce.workHours)) {
            workH = annonce.workHours;
          }
        }

        setActivities(activitiesList);
        setWorkHours(workH);
      } catch (err) {
        alert("Erreur de chargement des activités");
      }
    };
    if (visible) fetchData();
  }, [visible, annonceId, profilId]);

  // Heure de fin + contrôle horaires
  useEffect(() => {
    if (selectedActivity && date && heure) {
      const [h, m] = safeSplit(heure).map((x) => parseIntSafe(x));
      const startDate = new Date(date);
      startDate.setHours(h, m, 0, 0);

      const totalMin =
        (parseIntSafe(selectedActivity.durationHours) || 0) * 60 +
          parseIntSafe(selectedActivity.durationMinutes) ||
        parseIntSafe(selectedActivity.duration);

      const end = new Date(startDate.getTime() + (totalMin || 0) * 60000);
      setEndDate(end);

      let depasse = false;
      if (workHours.length > 0 && jourLabel) {
        const rawDay = workHours.find((wh) => (wh?.day || "").toLowerCase().startsWith(jourLabel));
        const { isOpen, slots } = normalizeWorkDay(rawDay);

        if (!isOpen || slots.length === 0) {
          depasse = true;
        } else {
          const fitsInOne = slots.some((seg) => {
            const segStart = toDateAtHHMM(startDate, seg?.start);
            const segEnd = toDateAtHHMM(startDate, seg?.end);
            if (!segStart || !segEnd) return false;
            return startDate >= segStart && end <= segEnd;
          });
          depasse = !fitsInOne;
        }
      } else {
        depasse = false;
      }
      setHorairesDepasse(depasse);
    } else {
      setEndDate(null);
      setHorairesDepasse(false);
    }
  }, [selectedActivity, date, heure, workHours, jourLabel]);

  // Enregistrement RDV
  const handleSave = async () => {
    if (loading || horairesDepasse) return;
    if (!selectedActivity) {
      alert("❌ Choisissez une activité.");
      return;
    }
    setLoading(true);

    const [h, m] = safeSplit(heure).map((x) => parseIntSafe(x));
    const start = new Date(date);
    start.setHours(h, m, 0, 0);

    const totalMin =
      (parseIntSafe(selectedActivity.durationHours) || 0) * 60 +
        parseIntSafe(selectedActivity.durationMinutes) ||
      parseIntSafe(selectedActivity.duration);
    const end = new Date(start.getTime() + (totalMin || 0) * 60000);

    if (bookedSlots && bookedSlots.some((slot) => start < new Date(slot.end) && end > new Date(slot.start))) {
      alert("❌ Ce créneau est déjà réservé !");
      setLoading(false);
      return;
    }

    // ===== Récup pro (annonce -> user) =====
    let proId = null;
    let proEmail = "";
    let proPhone = "";
    let proNom = "";
    let proSociete = "";
    let annonceAdresse = "";
    try {
      const annonce = await getJSON(`/api/annonces/${annonceId}`);

      proId = annonce.userId?._id || annonce.userId; // peut être objet ou string
      proSociete = annonce.nameCompany || annonce.userId?.nameCompany || annonce.userId?.societe || "";
      annonceAdresse = annonce?.adresse || annonce?.address || "";

      // 1) tenter avec l'annonce (au cas où)
      proEmail =
        annonce.userId?.email ||
        annonce.contactEmail ||
        annonce.email ||
        "";
      proPhone =
        annonce.userId?.telephone ||
        annonce.phoneNumber ||
        annonce.contactPhone ||
        "";

      // 2) si pas d'email → on va chercher le user par son id (inscription)
      if ((!proEmail || !String(proEmail).includes("@")) && proId) {
        const user = await getJSONAuth(`/api/users/${proId}`); // ton controller expose getUserById
        proEmail = user?.email || "";
        proPhone = user?.telephone || proPhone || "";
        proNom = user?.nom || user?.prenom ? `${user?.nom || ""}`.trim() : (proNom || "");
        proSociete = proSociete || user?.nameCompany || user?.societe || "";
      }

      proPhone = normalizePhone(proPhone);

      if (!proEmail || !String(proEmail).includes("@")) {
        const msg =
          "⚠️ Email du professionnel introuvable.\n\nOn a cherché dans l’annonce puis sur /api/users/:id.\n" +
          `proId=${proId}\nproEmail=${proEmail}\n\n` +
          "Vérifie que l’utilisateur (compte pro) a bien un email dans la BDD.";
        alert(msg);
        if (localStorage.getItem("debugNotifs") === "1") {
          console.error("[pro contact missing]", { proId, proEmail, proPhone, proNom, proSociete, annonceAdresse, annonce });
        }
        setLoading(false);
        return;
      }

      // debugNotifs (si besoin) : logs volontairement supprimés en prod
    } catch (e) {
      alert("Erreur pour trouver le professionnel, réessaie.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API}/api/slotsCalendar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          start,
          end,
          title: selectedActivity.name,
          description: `Client: ${clientPrenom} ${clientNom}, Email: ${clientEmail}, Tel: ${clientPhone}`,
          proId,
          profilId,
          userId,
          annonceId,
          emailUser: clientEmail,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        // ======= NOTIFS + RAPPELS =======
        const payload = {
          client: {
            email: clientEmail,
            phone: normalizePhone(clientPhone),
            nom: clientNom,
            prenom: clientPrenom,
          },
          pro: {
            email: proEmail,
            phone: proPhone,
            nom: proNom,
          },
          rdvISO: start.toISOString(),
          lieu: annonceAdresse || "",
          service: selectedActivity.name,
          price: selectedActivity.price ?? null,
          proSociete,
        };

        try {
          await Promise.all([notifyNow(payload), scheduleReminders(payload)]);
        } catch (e) {
          console.error("[notifs] error:", e);
        }

        alert("✅ Rendez-vous enregistré !");
        if (onSave) onSave();
        onClose();
      } else {
        alert(data.message || "Erreur lors de la réservation.");
      }
    } catch (err) {
      alert("Erreur réseau, veuillez réessayer.");
    }
    setLoading(false);
  };

  if (!visible) return null;

  return (
    <div className="popup-overlay">
      <div className="popup-content">
        <button className="popup-close-btn" onClick={onClose} disabled={loading}>
          &times;
        </button>
        <h3>Prendre rendez-vous</h3>

        <div>
          <strong>Date : </strong> {date}
        </div>
        <div>
          <strong>Heure : </strong> {heure}
        </div>
        <div>
          <strong>Nom :</strong> {clientPrenom} {clientNom}
          <br />
          <strong>Email :</strong> {clientEmail}
          <br />
          <strong>Téléphone :</strong> {clientPhone}
        </div>

        <label style={{ marginTop: 10 }}>Activité</label>
        <select
          value={selectedActivity?.id || ""}
          onChange={(e) => {
            const selected = activities.find((a) => a.id.toString() === e.target.value);
            setSelectedActivity(selected || null);
          }}
          disabled={loading}
        >
          <option value="">-- Choisir une activité --</option>
          {activities.map((activity) => (
            <option key={activity.id} value={activity.id}>
              {activity.name} ({activity.duration} min)
              {activity.price != null ? ` — ${formatEUR(activity.price)}` : ""}
            </option>
          ))}
        </select>

        {selectedActivity && endDate && (
          <div>
            <b>Heure de fin estimée :</b>{" "}
            {endDate.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
          </div>
        )}

        {horairesDepasse && (
          <div style={{ color: "red", fontWeight: 500, margin: "8px 0 3px 0" }}>
            ❌ Ce créneau dépasse les horaires d'ouverture du professionnel.
          </div>
        )}

        <div className="popup-actions">
          <button onClick={handleSave} disabled={loading || horairesDepasse}>
            {loading ? "Validation..." : "Valider"}
          </button>
          <button onClick={onClose} disabled={loading}>
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

export default PopupSlotClient;
